import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar as CalendarIcon, ArrowRight, Loader2, Bell } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { normalizeStage, stageOrder } from "../appShared";
import {
  fmtUSD,
  fmtUSDCompact,
  fmtRelativeDate,
  getPreviousLoginAt,
  stampLastLogin,
} from "./adminTheme";
import { isCustomerStage, computeAgeDays } from "./adminStages";
import { WelcomeSection } from "@/components/WelcomeSection";
import AddLeadModal from "./AddLeadModal";
import AdminDiagnosticsDashboard from "./AdminDiagnosticsDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DASHBOARD_MODE_STORAGE_KEY = "admin-dashboard-mode";

// ---- Layout primitives ---------------------------------------------------

const RevenueCard = ({ label, value, sub, accent, deltaText, deltaPositive = true }) => {
  // Top accent rail color & glow
  const railMap = {
    gold: "#007a9e",
    green: "#16a34a",
    orange: "#d97706",
  };
  const rail = railMap[accent] || "#007a9e";

  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-white border border-[#e5e7eb] shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Top accent rail */}
      <div className="h-[3px] w-full" style={{ backgroundColor: rail }} />
      <div className="px-7 py-7">
        <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase">
          {label}
        </div>
        <div
          className="mt-3 text-[36px] leading-none font-bold tracking-[-0.02em]"
          style={{ color: rail }}
        >
          {value}
        </div>
        <div className="mt-3 text-[12px] text-[#64748b]">{sub}</div>
        {deltaText && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11.5px] font-semibold"
            style={{
              backgroundColor: deltaPositive ? "rgba(22, 163, 74, 0.12)" : "rgba(220, 38, 38, 0.12)",
              color: deltaPositive ? "#15803d" : "#b91c1c",
            }}
          >
            <span>{deltaPositive ? "↑" : "↓"}</span>
            {deltaText}
          </div>
        )}
      </div>
    </div>
  );
};

const KPICard = ({ label, value, sub, valueColor, subAccent, dataTestid }) => (
  <div
    className="rounded-xl bg-white border border-[#e5e7eb] p-5 transition-shadow hover:shadow-sm"
    data-testid={dataTestid}
  >
    <div className="text-[11.5px] font-semibold tracking-[0.14em] text-[#4b5563] uppercase">
      {label}
    </div>
    <div
      className="mt-2 text-[34px] font-bold tracking-[-0.02em] leading-none"
      style={{ color: valueColor || "#0f172a" }}
    >
      {value}
    </div>
    {sub && (
      <div className="mt-3 text-[12px] text-[#64748b]">
        {subAccent ? (
          <>
            <span className="font-semibold" style={{ color: subAccent.color }}>{subAccent.text}</span>
            {" "}{sub}
          </>
        ) : sub}
      </div>
    )}
  </div>
);

const FunnelRow = ({ label, count, value, color, max }) => {
  const pct = max > 0 ? Math.max(8, (count / max) * 100) : 8;
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="text-[13px] text-[#475569] w-[150px] shrink-0">{label}</div>
      <div className="flex-1 h-[10px] rounded-full bg-[#eef2f7] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <div className="text-[13px] font-bold text-[#0f172a] w-6 text-right">{count}</div>
      <div className="text-[12px] text-[#4b5563] w-[80px] text-right tabular-nums">{value}</div>
    </div>
  );
};

const AttentionItem = ({ company, message, severity, action, onAction }) => {
  const palette = {
    high: { dot: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
    medium: { dot: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
    low: { dot: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  }[severity] || { dot: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" };

  return (
    <div
      className="flex items-start justify-between gap-3 px-4 py-3 rounded-xl border"
      style={{ backgroundColor: palette.bg, borderColor: palette.border }}
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="mt-1.5 inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: palette.dot }} />
        <div className="min-w-0">
          <span className="text-[13px] font-semibold text-[#0f172a]">{company}</span>
          <span className="text-[13px] text-[#475569]"> — {message}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onAction}
        className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-[#007a9e] hover:text-[#00607d]"
      >
        {action}
        <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
};

// ---- Main Dashboard ------------------------------------------------------

export default function AdminDashboard({ session, business }) {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [mrrData, setMrrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previousLoginAt] = useState(() => getPreviousLoginAt());
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(DASHBOARD_MODE_STORAGE_KEY);
      return stored === "diagnostics" ? "diagnostics" : "assessments";
    } catch (_e) {
      return "assessments";
    }
  });

  const updateMode = (next) => {
    setMode(next);
    try { localStorage.setItem(DASHBOARD_MODE_STORAGE_KEY, next); } catch (_e) { /* ignore */ }
  };

  // Stage 7: each business route forces its mode; the toggle is hidden (kept for
  // the ADMIN_NAV_V2=false fallback and for the /app index redirect's stored key).
  const forcedMode = business === "myschoolrx" ? "diagnostics" : business === "clarity" ? "assessments" : null;
  const effectiveMode = forcedMode || mode;
  const showToggle = !business;
  const eyebrow = business === "clarity" ? "Clarity · Overview" : business === "myschoolrx" ? "MySchoolRx · Overview" : null;

  const headers = useMemo(() => ({
    "x-tenant-id": session?.tenant?.id || "tenant_1",
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
  }), [session]);

  const [failedEmails, setFailedEmails] = useState(0);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/notifications?limit=200`, { headers })
      .then((r) => r.ok ? r.json() : [])
      .then((rows) => setFailedEmails((rows || []).filter((n) => n.status === "failed").length))
      .catch(() => {});
  }, [headers]);

  const adminName = (session?.user?.name || session?.user?.full_name || session?.user?.email || "").split(/[ @]/)[0];
  const adminWelcomeItems = failedEmails > 0 ? [{
    signature: "admin:failed_emails", priority: 1,
    lead: `${failedEmails} email notification${failedEmails === 1 ? "" : "s"} failed to send — review the delivery issue.`,
    actions: [{ label: "Review issues", primary: true, href: "/app/platform/emails" }],
  }] : [];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/prospects`, { headers }).then((r) => r.json()).catch(() => []),
      fetch(`${BACKEND_URL}/api/pricing/public`).then((r) => r.json()).catch(() => null),
      fetch(`${BACKEND_URL}/api/prospects/mrr-breakdown`, { headers }).then((r) => r.json()).catch(() => null),
    ])
      .then(([list, pr, mrr]) => {
        setProspects(Array.isArray(list) ? list : []);
        setPricing(pr);
        setMrrData(mrr);
      })
      .catch(() => toast.error("Could not load dashboard"))
      .finally(() => setLoading(false));

    // Stamp last login *after* we capture the previous value above
    stampLastLogin();
  }, [headers]);

  // ---------------- Derived metrics ----------------
  const assessmentPriceRaw = pricing?.pricing?.assessment?.amount;
  const assessmentPriceSet = Number.isFinite(assessmentPriceRaw) && assessmentPriceRaw > 0;
  const assessmentPrice = assessmentPriceSet ? assessmentPriceRaw : 4995;
  const implementationPrice = pricing?.pricing?.implementation?.amount || 9995;
  const retainerPlans = pricing?.retainer_plans || [];
  const defaultAvgRetainer = retainerPlans.length > 0
    ? retainerPlans.reduce((s, p) => s + (p.monthly_price || 0), 0) / retainerPlans.length
    : 3500;

  // Pipeline & delivery counts
  const newIntakes = prospects.filter((p) => normalizeStage(p.stage) === "NEW_INTAKE").length;
  const newIntakesAwaitingQual = prospects.filter((p) =>
    normalizeStage(p.stage) === "NEW_INTAKE" && computeAgeDays(p.created_at) >= 2,
  ).length;

  const qualified = prospects.filter((p) => {
    const s = normalizeStage(p.stage);
    return ["QUALIFIED", "QUAL_CALL_BOOKED", "QUALIFIED_TO_BUY", "FREE_ASSESSMENT_COMPLETE"].includes(s);
  }).length;

  const assessmentsSold = prospects.filter((p) => {
    const s = normalizeStage(p.stage);
    return ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"].includes(s);
  });
  const assessmentsSoldUnassigned = assessmentsSold.filter((p) => !p.assigned_consultant_id && !p.assigned_to_user_id).length;

  const projectsInDelivery = prospects.filter((p) =>
    ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY"].includes(normalizeStage(p.stage)),
  );
  const projectsAtRisk = projectsInDelivery.filter((p) => p.project_health_percent !== undefined && p.project_health_percent < 60).length;
  const projectsOnTrack = projectsInDelivery.length - projectsAtRisk;

  // Customers
  const customers = prospects.filter((p) => isCustomerStage(p.stage));
  const customersOnRetainer = prospects.filter((p) => normalizeStage(p.stage) === "RETAINER_ACTIVE").length;

  // Revenue Booked (all-time)
  const revenueBooked = prospects.reduce((sum, p) => {
    let total = sum;
    const s = normalizeStage(p.stage);
    if (stageOrder[s] >= stageOrder.ASSESSMENT_PURCHASED) total += assessmentPrice;
    if (stageOrder[s] >= stageOrder.PROJECT_IN_DELIVERY) total += implementationPrice;
    return total;
  }, 0);

  // MRR
  let monthlyRecurring = 0;
  if (mrrData?.retainers?.length) {
    monthlyRecurring = mrrData.retainers.reduce((sum, r) => sum + (r.monthly_value || defaultAvgRetainer), 0);
  } else {
    monthlyRecurring = customersOnRetainer * defaultAvgRetainer;
  }

  // Projected this quarter — simple heuristic = qualified + assessmentsSold + scope confirmed
  const projectedThisQuarter = (() => {
    let p = 0;
    prospects.forEach((pr) => {
      const s = normalizeStage(pr.stage);
      if (s === "QUALIFIED_TO_BUY" || s === "FREE_ASSESSMENT_COMPLETE") p += assessmentPrice * 0.3; // weighted
      if (s === "ASSESSMENT_PURCHASED" || s === "ASSESSMENT_IN_PROGRESS") p += implementationPrice * 0.4;
      if (s === "ASSESSMENT_DELIVERED" || s === "SCOPE_CONFIRMED" || s === "SOW_SENT") p += implementationPrice * 0.6;
      if (s === "SOW_APPROVED") p += implementationPrice * 0.85;
    });
    return Math.round(p);
  })();

  // Avg deal value across closed projects
  const closedDeals = prospects.filter((p) => stageOrder[normalizeStage(p.stage)] >= stageOrder.PROJECT_IN_DELIVERY);
  const avgDealValue = closedDeals.length > 0
    ? Math.round(closedDeals.reduce((s) => s + assessmentPrice + implementationPrice, 0) / closedDeals.length)
    : 0;

  // Lead → Customer rate
  const totalLeads = prospects.length;
  const leadToCustomerRate = totalLeads > 0 ? Math.round((customers.length / totalLeads) * 100) : 0;

  // Retainer conversion rate
  const projectComplete = prospects.filter((p) =>
    ["PROJECT_DELIVERED", "PROJECT_COMPLETED", "RETAINER_OFFERED", "RETAINER_ACTIVE"].includes(normalizeStage(p.stage)),
  ).length;
  const retainerConversionRate = projectComplete > 0 ? Math.round((customersOnRetainer / projectComplete) * 100) : 0;

  // ---------------- Pipeline funnel data ----------------
  const funnelStages = [
    {
      key: "NEW_INTAKE", label: "New Intake", color: "#93c5fd",
      stages: ["NEW_INTAKE"],
    },
    {
      key: "QUALIFIED", label: "Qualified", color: "#60a5fa",
      stages: ["QUALIFIED", "QUAL_CALL_BOOKED", "QUALIFIED_TO_BUY", "FREE_ASSESSMENT_COMPLETE"],
    },
    {
      key: "ASSESS", label: "Assessment Purchased", color: "#007a9e",
      stages: ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS"],
    },
    {
      key: "SCOPE", label: "Scope Confirmation", color: "#E5BD63",
      stages: ["ASSESSMENT_DELIVERED", "SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED"],
    },
    {
      key: "PROJECT", label: "Project in Delivery", color: "#16a34a",
      stages: ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY"],
    },
    {
      key: "RETAINER", label: "On Retainer", color: "#15803d",
      stages: ["RETAINER_ACTIVE"],
    },
  ];

  const funnelData = funnelStages.map((stage) => {
    const items = prospects.filter((p) => stage.stages.includes(normalizeStage(p.stage)));
    const value = stage.key === "RETAINER"
      ? items.length * defaultAvgRetainer  // monthly value
      : items.reduce((sum, p) => {
        const s = normalizeStage(p.stage);
        let v = 0;
        if (stageOrder[s] >= stageOrder.PROJECT_IN_DELIVERY) v += implementationPrice;
        if (stageOrder[s] >= stageOrder.ASSESSMENT_PURCHASED) v += assessmentPrice;
        if (stageOrder[s] < stageOrder.ASSESSMENT_PURCHASED) v += assessmentPrice; // pipeline value
        return sum + v;
      }, 0);
    return { ...stage, count: items.length, value };
  });
  const funnelMaxCount = Math.max(1, ...funnelData.map((f) => f.count));

  // ---------------- Needs Your Attention ----------------
  const attentionItems = useMemo(() => {
    const items = [];

    // 1. Projects in delivery without consultant assigned
    projectsInDelivery.forEach((p) => {
      const ageDays = computeAgeDays(p.updated_at || p.created_at);
      if (!p.assigned_consultant_id && !p.assigned_to_user_id && ageDays >= 2) {
        items.push({
          id: `unassigned-${p.id}`,
          severity: "high",
          company: p.account?.name || p.company_name || "Unknown",
          message: `no consultant assigned ${ageDays} days post-kickoff. Client satisfaction at risk.`,
          action: "Assign",
          onAction: () => navigate(`/app/customers#${p.id}`),
        });
      }
    });

    // 2. Assessments purchased without consultant (revenue stalling)
    if (assessmentsSoldUnassigned > 0) {
      items.push({
        id: "assess-unassigned",
        severity: "medium",
        company: `${assessmentsSoldUnassigned} assessments purchased`,
        message: "no consultant assigned yet. Revenue stalling.",
        action: "Review",
        onAction: () => navigate("/app/customers"),
      });
    }

    // 3. Stale leads (>5 days no activity)
    prospects
      .filter((p) => {
        const s = normalizeStage(p.stage);
        return ["QUALIFIED", "QUAL_CALL_BOOKED", "QUALIFIED_TO_BUY"].includes(s) && computeAgeDays(p.updated_at) >= 5;
      })
      .slice(0, 1)
      .forEach((p) => {
        const ageDays = computeAgeDays(p.updated_at);
        items.push({
          id: `stale-${p.id}`,
          severity: "medium",
          company: p.account?.name || p.company_name || "Unknown",
          message: `qualification outstanding ${ageDays} days. Milestone delayed.`,
          action: "Follow Up",
          onAction: () => navigate(`/app/pipeline#${p.id}`),
        });
      });

    // 4. Project complete → retainer not offered
    prospects
      .filter((p) => ["PROJECT_DELIVERED", "PROJECT_COMPLETED"].includes(normalizeStage(p.stage)))
      .slice(0, 1)
      .forEach((p) => {
        items.push({
          id: `retainer-${p.id}`,
          severity: "low",
          company: p.account?.name || p.company_name || "Unknown",
          message: "project complete. Prime candidate for retainer conversion.",
          action: "Send Offer",
          onAction: () => navigate(`/app/customers#${p.id}`),
        });
      });

    return items.slice(0, 5);
  }, [prospects, navigate, assessmentsSoldUnassigned, projectsInDelivery]);

  // ---------------- Recent Activity ----------------
  const recentActivity = useMemo(() => {
    return prospects
      .filter((p) => p.account?.name)
      .slice()
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .slice(0, 5)
      .map((p) => {
        const stage = normalizeStage(p.stage);
        let action = "updated";
        if (stage === "NEW_INTAKE") action = "new intake received. Awaiting qualification.";
        else if (stage === "ASSESSMENT_PURCHASED") action = "assessment purchased. Consultant assignment needed.";
        else if (stage === "ASSESSMENT_DELIVERED") action = "assessment delivered. Awaiting scope confirmation.";
        else if (stage === "SCOPE_CONFIRMED" || stage === "SOW_APPROVED") action = "scope confirmed. Ready for project kickoff.";
        else if (stage === "PROJECT_IN_DELIVERY") action = "weekly check-in completed. Testing phase on track.";
        else if (stage === "PROJECT_DELIVERED") action = "project delivered. Ready for retainer offer.";
        else if (stage === "RETAINER_ACTIVE") action = "retainer active. Monthly check-in maintained.";
        return {
          id: p.id,
          name: p.account?.name || "—",
          initials: (p.account?.name || "—").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
          action,
          ts: p.updated_at || p.created_at,
        };
      });
  }, [prospects]);

  // ---------------- Since you last logged in (categorized briefing) ----------------
  const sinceBriefing = useMemo(() => {
    if (!previousLoginAt) return { categories: [], total: 0 };
    const previousMs = new Date(previousLoginAt).getTime();
    const buckets = {
      pipeline: { key: "pipeline", label: "Pipeline", color: "#3b82f6", items: [] },
      revenue: { key: "revenue", label: "Revenue", color: "#16a34a", items: [] },
      delivery: { key: "delivery", label: "Delivery", color: "#007a9e", items: [] },
    };
    prospects.forEach((p) => {
      const ts = new Date(p.updated_at || p.created_at).getTime();
      if (ts <= previousMs) return;
      const stage = normalizeStage(p.stage);
      const company = p.account?.name || p.company_name || "Unknown";
      if (stage === "NEW_INTAKE") {
        buckets.pipeline.items.push({ id: `intake-${p.id}`, label: `New lead: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "QUALIFIED" || stage === "QUAL_CALL_BOOKED" || stage === "QUALIFIED_TO_BUY") {
        buckets.pipeline.items.push({ id: `qual-${p.id}`, label: `Qualified: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "FREE_ASSESSMENT_COMPLETE") {
        buckets.pipeline.items.push({ id: `free-${p.id}`, label: `Free assessment delivered: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "ASSESSMENT_PURCHASED") {
        buckets.revenue.items.push({ id: `pay-${p.id}`, label: `In-Depth Assessment purchased: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "ASSESSMENT_DELIVERED") {
        buckets.delivery.items.push({ id: `deliv-${p.id}`, label: `Detailed report delivered: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "SCOPE_CONFIRMED") {
        buckets.delivery.items.push({ id: `scope-${p.id}`, label: `Scope confirmed: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "SOW_SENT") {
        buckets.delivery.items.push({ id: `sowsent-${p.id}`, label: `SOW sent: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "SOW_APPROVED") {
        buckets.revenue.items.push({ id: `sow-${p.id}`, label: `SOW approved: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "PROJECT_KICKOFF" || stage === "PROJECT_ACTIVE" || stage === "PROJECT_IN_DELIVERY") {
        buckets.delivery.items.push({ id: `proj-${p.id}`, label: `Project active: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "PROJECT_DELIVERED" || stage === "PROJECT_COMPLETED") {
        buckets.delivery.items.push({ id: `done-${p.id}`, label: `Project delivered: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "RETAINER_ACTIVE") {
        buckets.revenue.items.push({ id: `ret-${p.id}`, label: `Retainer started: ${company}`, ts, prospect_id: p.id });
      } else if (stage === "AT_RISK" || stage === "CHURNED") {
        buckets.delivery.items.push({ id: `risk-${p.id}`, label: `Account flagged at-risk: ${company}`, ts, prospect_id: p.id, severity: "high" });
      }
    });
    // Sort each bucket by recency, cap items per bucket so no single category dominates
    const PER_BUCKET = 5;
    Object.values(buckets).forEach((b) => {
      b.items.sort((a, b2) => b2.ts - a.ts);
      b.items = b.items.slice(0, PER_BUCKET);
    });
    const categories = Object.values(buckets).filter((b) => b.items.length > 0);
    const total = categories.reduce((sum, c) => sum + c.items.length, 0);
    return { categories, total };
  }, [prospects, previousLoginAt]);

  // ---------------- Render ----------------
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const updatedAt = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  // Segmented mode toggle (Assessments | Diagnostics), persisted to localStorage
  const ModeToggle = (
    <div
      className="inline-flex items-center rounded-lg border border-[#e5e7eb] bg-white p-1"
      data-testid="dashboard-mode-toggle"
    >
      {[
        { key: "assessments", label: "Sales" },
        { key: "diagnostics", label: "Diagnostics" },
      ].map((opt) => {
        const active = mode === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => updateMode(opt.key)}
            className={`px-4 h-8 rounded-md text-[12px] font-semibold transition-colors ${
              active ? "text-white" : "text-[#475569] hover:text-[#00698a]"
            }`}
            style={active ? { backgroundColor: "#007a9e" } : undefined}
            data-testid={`dashboard-mode-${opt.key}`}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-7" data-testid="admin-dashboard">
      <WelcomeSection name={adminName} items={adminWelcomeItems} headers={{ "x-session-token": session?.session_token || "" }} testid="admin-welcome" />
      {/* Page Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          {eyebrow && (
            <div className="text-[11.5px] font-bold tracking-[0.14em] uppercase text-[#00698a]" data-testid="overview-eyebrow">{eyebrow}</div>
          )}
          <h1 className="mt-1 text-[28px] font-bold tracking-[-0.02em] text-[#0f172a]" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="mt-1 text-[13px] text-[#64748b]">
            {effectiveMode === "diagnostics"
              ? `Diagnostic operations — updated today at ${updatedAt}`
              : `Business overview — updated today at ${updatedAt}`}
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          {showToggle && ModeToggle}
          <span
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-medium text-[#475569]"
            data-testid="dashboard-date-pill"
          >
            <CalendarIcon className="h-4 w-4 text-[#4b5563]" />
            {today}
          </span>
          {effectiveMode === "assessments" && (
            <button
              type="button"
              onClick={() => setAddLeadOpen(true)}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-lg text-[13px] font-semibold text-white shadow-[0_4px_12px_rgba(0,122,158,0.30)] hover:opacity-95 transition-opacity"
              style={{ backgroundColor: "#007a9e" }}
              data-testid="dashboard-add-lead-btn"
            >
              <Plus className="h-4 w-4" /> New Lead
            </button>
          )}
        </div>
      </div>

      {effectiveMode === "diagnostics" ? (
        <div data-testid="admin-myschoolrx-overview">
          <AdminDiagnosticsDashboard session={session} />
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#4b5563]" />
        </div>
      ) : (
        <div data-testid="admin-clarity-overview">
        <AssessmentsDashboardBody
          sinceBriefing={sinceBriefing}
          previousLoginAt={previousLoginAt}
          revenueBooked={revenueBooked}
          monthlyRecurring={monthlyRecurring}
          customersOnRetainer={customersOnRetainer}
          defaultAvgRetainer={defaultAvgRetainer}
          projectedThisQuarter={projectedThisQuarter}
          newIntakes={newIntakes}
          newIntakesAwaitingQual={newIntakesAwaitingQual}
          qualified={qualified}
          assessmentsSold={assessmentsSold}
          assessmentsSoldUnassigned={assessmentsSoldUnassigned}
          assessmentPrice={assessmentPrice}
          assessmentPriceSet={assessmentPriceSet}
          projectsInDelivery={projectsInDelivery}
          projectsAtRisk={projectsAtRisk}
          projectsOnTrack={projectsOnTrack}
          customers={customers}
          avgDealValue={avgDealValue}
          leadToCustomerRate={leadToCustomerRate}
          retainerConversionRate={retainerConversionRate}
          funnelData={funnelData}
          funnelMaxCount={funnelMaxCount}
          attentionItems={attentionItems}
          recentActivity={recentActivity}
          prospects={prospects}
          navigate={navigate}
        />
        </div>
      )}

      <AddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onCreated={(p) => { if (p?.id) navigate(`/app/leads/${p.id}`); }}
      />
    </div>
  );
}

// ---- Assessments dashboard body (extracted so the toggle can swap views) ----
function AssessmentsDashboardBody({
  sinceBriefing,
  previousLoginAt,
  revenueBooked,
  monthlyRecurring,
  customersOnRetainer,
  defaultAvgRetainer,
  projectedThisQuarter,
  newIntakes,
  newIntakesAwaitingQual,
  qualified,
  assessmentsSold,
  assessmentsSoldUnassigned,
  assessmentPrice,
  assessmentPriceSet,
  projectsInDelivery,
  projectsAtRisk,
  projectsOnTrack,
  customers,
  avgDealValue,
  leadToCustomerRate,
  retainerConversionRate,
  funnelData,
  funnelMaxCount,
  attentionItems,
  recentActivity,
  prospects,
  navigate,
}) {
  return (
    <>
      {/* "Since you last logged in" briefing — categorized */}
      {sinceBriefing.total > 0 && (
        <div
          className="rounded-2xl p-5 border"
          style={{ backgroundColor: "#fffbeb", borderColor: "#fde68a" }}
          data-testid="since-last-login"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: "#007a9e" }}>
              <Bell className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-[13px] font-bold text-[#0f172a]">
                  Since you last logged in <span className="text-[#4b5563] font-normal">({fmtRelativeDate(previousLoginAt)})</span>
                </div>
                <div className="text-[11.5px] font-semibold tracking-[0.10em] uppercase text-[#92400e]">
                  {sinceBriefing.total} update{sinceBriefing.total === 1 ? "" : "s"}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {sinceBriefing.categories.map((cat) => (
                  <div key={cat.key} data-testid={`since-cat-${cat.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="text-[11.5px] font-bold tracking-[0.12em] uppercase text-[#475569]">
                        {cat.label}
                      </span>
                      <span className="text-[11.5px] font-bold tracking-[0.04em] uppercase text-[#4b5563]">
                        ({cat.items.length})
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {cat.items.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => it.prospect_id && navigate(`/app/${normalizeStage(prospects.find((p) => p.id === it.prospect_id)?.stage || "")?.startsWith("PROJECT_") || isCustomerStage(prospects.find((p) => p.id === it.prospect_id)?.stage) ? "customers" : "pipeline"}/${it.prospect_id}`)}
                          className="text-left text-[12px] text-[#0f172a] px-3 py-1.5 rounded-md bg-white border border-[#fde68a] hover:bg-[#fffbeb] transition-colors"
                          data-testid={`since-item-${it.id}`}
                        >
                          <span className="line-clamp-1">{it.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue strip — 3 dark cards */}
      <div>
        <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase mb-3">
          Revenue
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <RevenueCard
            label="Revenue Booked (All Time)"
            value={fmtUSD(revenueBooked)}
            sub="One-time projects + retainers"
            accent="gold"
          />
          <RevenueCard
            label="Active MRR"
            value={fmtUSD(monthlyRecurring)}
            sub={`${customersOnRetainer} retainer accounts × ${fmtUSD(defaultAvgRetainer)}/mo`}
            accent="green"
          />
          <RevenueCard
            label="Projected This Quarter"
            value={fmtUSD(projectedThisQuarter)}
            sub="Based on pipeline × close rates"
            accent="orange"
          />
        </div>
      </div>

      {/* Pipeline & Delivery — 8 KPI cards */}
      <div>
        <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase mb-3">
          Pipeline & Delivery
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            label="New Intakes"
            value={newIntakes}
            sub="awaiting qualification >48h"
            subAccent={newIntakesAwaitingQual > 0 ? { text: newIntakesAwaitingQual, color: "#f59e0b" } : null}
            valueColor="#0f172a"
            dataTestid="kpi-new-intakes"
          />
          <KPICard
            label="Qualified Opportunities"
            value={qualified}
            sub="Ready for assessment purchase"
            valueColor="#0f172a"
            dataTestid="kpi-qualified"
          />
          <KPICard
            label="Assessments Sold"
            value={assessmentsSold.length}
            sub={assessmentPriceSet ? "unassigned" : "set in Pricing"}
            subAccent={
              !assessmentPriceSet
                ? { text: "Price not set", color: "#92400e" }
                : assessmentsSoldUnassigned > 0
                ? { text: `${fmtUSD(assessmentPrice)} each · ${assessmentsSoldUnassigned}`, color: "#007a9e" }
                : { text: `${fmtUSD(assessmentPrice)} each`, color: "#007a9e" }
            }
            valueColor="#007a9e"
            dataTestid="kpi-assessments-sold"
          />
          <KPICard
            label="Projects Active"
            value={projectsInDelivery.length}
            sub={`at risk · ${projectsOnTrack} on track`}
            subAccent={{ text: projectsAtRisk, color: "#dc2626" }}
            valueColor="#16a34a"
            dataTestid="kpi-projects-active"
          />
          <KPICard
            label="Total Customers"
            value={customers.length}
            sub="on retainer"
            subAccent={{ text: customersOnRetainer, color: "#007a9e" }}
            valueColor="#0f172a"
            dataTestid="kpi-total-customers"
          />
          <KPICard
            label="Avg Deal Value"
            value={fmtUSDCompact(avgDealValue)}
            sub="Across closed projects"
            valueColor="#0f172a"
            dataTestid="kpi-avg-deal-value"
          />
          <KPICard
            label="Lead → Customer Rate"
            value={`${leadToCustomerRate}%`}
            sub="Leads that became customers"
            valueColor="#16a34a"
            dataTestid="kpi-lead-customer-rate"
          />
          <KPICard
            label="Retainer Conversion"
            value={`${retainerConversionRate}%`}
            sub="Of completed projects → retainer"
            valueColor="#007a9e"
            dataTestid="kpi-retainer-conversion"
          />
        </div>
      </div>

      {/* Funnel + Needs Your Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-5">
        {/* Pipeline Funnel */}
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6" data-testid="pipeline-funnel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[16px] font-bold text-[#0f172a]">Pipeline Funnel</h2>
            <button
              type="button"
              onClick={() => navigate("/app/pipeline")}
              className="text-[12px] font-semibold text-[#007a9e] hover:text-[#00607d] inline-flex items-center gap-1"
            >
              View Leads <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="space-y-1">
            {funnelData.map((f) => (
              <FunnelRow
                key={f.key}
                label={f.label}
                count={f.count}
                value={f.key === "RETAINER" ? `${fmtUSDCompact(f.value)}/mo` : fmtUSDCompact(f.value)}
                color={f.color}
                max={funnelMaxCount}
              />
            ))}
          </div>
        </div>

        {/* Needs Your Attention */}
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6" data-testid="needs-attention">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[16px] font-bold text-[#0f172a]">Needs Your Attention</h2>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold"
              style={{ backgroundColor: "#fef3c7", color: "#a16207" }}
            >
              {attentionItems.length} item{attentionItems.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-2.5">
            {attentionItems.length === 0 ? (
              <div className="text-[13px] text-[#4b5563] py-6 text-center">
                Nothing flagged right now. Stay sharp.
              </div>
            ) : (
              attentionItems.map((it) => (
                <AttentionItem
                  key={it.id}
                  company={it.company}
                  message={it.message}
                  severity={it.severity}
                  action={it.action}
                  onAction={it.onAction}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6" data-testid="recent-activity">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-bold text-[#0f172a]">Recent Activity</h2>
          <button
            type="button"
            className="text-[12px] font-semibold text-[#007a9e] hover:text-[#00607d] inline-flex items-center gap-1"
            onClick={() => navigate("/app/customers")}
          >
            View All <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="space-y-3">
          {recentActivity.length === 0 ? (
            <div className="text-[13px] text-[#4b5563] py-6 text-center">
              No activity yet.
            </div>
          ) : (
            recentActivity.map((it, idx) => (
              <div key={`${it.id}-${idx}`} className="flex items-start gap-3">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11.5px] font-bold text-[#16a34a]"
                  style={{ backgroundColor: "#dcfce7" }}
                >
                  {it.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#0f172a]">
                    <span className="font-bold">{it.name}</span>{" "}
                    <span className="text-[#475569]">— {it.action}</span>
                  </div>
                  <div className="text-[12px] text-[#4b5563] mt-0.5">{fmtRelativeDate(it.ts)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
